import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getTodayStr } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  CreditCard,
  Trash2,
  Pause,
  Play,
  Save,
  Repeat
} from "lucide-react";

const CATEGORIES = [
  { value: "housing", label: "Housing" },
  { value: "utilities", label: "Utilities" },
  { value: "food", label: "Food & Groceries" },
  { value: "transport", label: "Transport" },
  { value: "entertainment", label: "Entertainment" },
  { value: "healthcare", label: "Healthcare" },
  { value: "insurance", label: "Insurance" },
  { value: "subscriptions", label: "Subscriptions" },
  { value: "debt", label: "Debt Payment" },
  { value: "other", label: "Other" }
];

const FREQUENCIES = [
  { value: "once", label: "One-off (just once)" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" }
];

export default function EditTransactionSheet({ 
  isOpen, 
  onClose, 
  transaction, 
  transactionType, // 'income', 'bill', 'debt'
  currency,
  onUpdate,
  isMobile
}) {
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    frequency: "monthly",
    startDate: "",
    category: "other",
    isRecurring: true,
    totalDebt: "",
    interestRate: "",
    status: "active",
    linkedDebtId: ""
  });

  // Determine the actual type based on transaction data
  const getActualType = () => {
    if (transactionType === 'income') return 'income';
    if (transaction?.is_debt) return 'debt';
    return 'bill';
  };

  const actualType = getActualType();

  // Initialize form when transaction changes
  useEffect(() => {
    if (transaction) {
      const today = getTodayStr();
      
      if (transactionType === 'income') {
        setFormData({
          name: transaction.name || "",
          amount: transaction.amount?.toString() || "",
          frequency: transaction.frequency || "monthly",
          startDate: transaction.next_pay_date || transaction.date || today,
          category: "other",
          isRecurring: transaction.is_recurring !== false && transaction.frequency !== 'once',
          totalDebt: "",
          interestRate: "",
          status: transaction.status || "active"
        });
      } else {
        // For bills, prefer start_date, fallback to date (calendar occurrence), then today
        // Check if recurring: explicitly true, or not explicitly false AND not a 'once' frequency
        const isRecurring = transaction.is_recurring === true ? true : 
          (transaction.is_recurring === false ? false : transaction.frequency !== 'once');
        setFormData({
          name: transaction.name || "",
          amount: transaction.amount?.toString() || "",
          frequency: transaction.frequency || "monthly",
          startDate: transaction.start_date || transaction.date || today,
          category: transaction.category || "other",
          isRecurring: isRecurring,
          totalDebt: transaction.total_debt?.toString() || "",
          interestRate: transaction.interest_rate?.toString() || "",
          status: transaction.status || "active",
          linkedDebtId: transaction.linked_debt_id || ""
        });
      }
    }
  }, [transaction, transactionType]);

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.amount) {
      toast.error("Please fill in name and amount");
      return;
    }
    
    if (!formData.startDate) {
      toast.error("Please select a date");
      return;
    }

    setLoading(true);
    try {
      if (transactionType === 'income') {
        await axios.put(`${API}/income/${transaction.income_id}`, {
          name: formData.name,
          amount: parseFloat(formData.amount),
          frequency: formData.isRecurring ? formData.frequency : 'once',
          next_pay_date: formData.startDate,
          is_recurring: formData.isRecurring
        }, { withCredentials: true });
      } else {
        await axios.put(`${API}/bills/${transaction.bill_id}`, {
          name: formData.name,
          amount: parseFloat(formData.amount),
          frequency: formData.isRecurring ? formData.frequency : 'once',
          start_date: formData.startDate,
          category: formData.category,
          is_recurring: formData.isRecurring,
          is_debt: actualType === 'debt',
          total_debt: actualType === 'debt' ? parseFloat(formData.totalDebt) || null : null,
          interest_rate: actualType === 'debt' ? parseFloat(formData.interestRate) || null : null,
          // Include linked_debt_id (send null to clear)
          linked_debt_id: formData.linkedDebtId || null
        }, { withCredentials: true });
      }
      
      toast.success("Entry updated successfully");
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error updating:', error);
      toast.error("Failed to update entry");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (transactionType === 'income') {
        await axios.delete(`${API}/income/${transaction.income_id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/bills/${transaction.bill_id}`, { withCredentials: true });
      }
      
      toast.success("Entry deleted successfully");
      setDeleteDialogOpen(false);
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error("Failed to delete entry");
    } finally {
      setLoading(false);
    }
  };

  const handlePauseResume = async () => {
    setLoading(true);
    try {
      const isPaused = formData.status === 'paused';
      const endpoint = transactionType === 'income' 
        ? `${API}/income/${transaction.income_id}/${isPaused ? 'resume' : 'pause'}`
        : `${API}/bills/${transaction.bill_id}/${isPaused ? 'resume' : 'pause'}`;
      
      await axios.post(endpoint, {}, { withCredentials: true });
      
      toast.success(isPaused ? "Entry resumed" : "Entry paused");
      onUpdate?.();
      onClose();
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast.error("Failed to update entry");
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = () => {
    if (actualType === 'income') return 'emerald';
    if (actualType === 'debt') return 'red';
    return 'amber';
  };

  const color = getTypeColor();

  const sheetContent = (
    <div className={`h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isMobile ? 'rounded-t-3xl' : ''} overflow-hidden`}>
      {isMobile && (
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1.5 bg-white/30 rounded-full" />
        </div>
      )}
      
      {/* Header */}
      <div className="px-6 pt-4 pb-4 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl bg-${color}-500/20 flex items-center justify-center`}>
              {actualType === 'income' && <TrendingUp className={`w-5 h-5 text-${color}-400`} />}
              {actualType === 'bill' && <TrendingDown className={`w-5 h-5 text-${color}-400`} />}
              {actualType === 'debt' && <CreditCard className={`w-5 h-5 text-${color}-400`} />}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">
                Edit {actualType === 'income' ? 'Income' : actualType === 'debt' ? 'Debt' : 'Bill'}
              </h2>
              {formData.isRecurring && (
                <p className="text-xs text-white/50 flex items-center gap-1">
                  <Repeat className="w-3 h-3" /> Recurring entry
                </p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full bg-white/10 text-white/60 hover:bg-white/20">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="space-y-5">
          {/* Name */}
          <div>
            <Label className="text-sm text-white/70 mb-2 block">Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter name..."
              className="bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40"
              data-testid="edit-name-input"
            />
          </div>

          {/* Amount */}
          <div>
            <Label className="text-sm text-white/70 mb-2 block">Amount</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
                {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'}
              </span>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="0.00"
                className="pl-8 bg-white/10 border-white/20 text-white placeholder:text-white/30 focus:border-white/40"
                data-testid="edit-amount-input"
              />
            </div>
          </div>

          {/* Recurring Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3">
              <Repeat className="w-5 h-5 text-white/50" />
              <div>
                <p className="text-sm font-medium text-white">Recurring</p>
                <p className="text-xs text-white/40">Repeats on a schedule</p>
              </div>
            </div>
            <Switch
              checked={formData.isRecurring}
              onCheckedChange={(checked) => setFormData(prev => ({ 
                ...prev, 
                isRecurring: checked,
                frequency: checked ? (prev.frequency === 'once' ? 'monthly' : prev.frequency) : 'once'
              }))}
              data-testid="edit-recurring-switch"
            />
          </div>

          {/* Frequency (only if recurring) */}
          {formData.isRecurring && (
            <div>
              <Label className="text-sm text-white/70 mb-2 block">Frequency</Label>
              <Select 
                value={formData.frequency} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="edit-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.filter(f => f.value !== 'once').map((freq) => (
                    <SelectItem key={freq.value} value={freq.value}>
                      {freq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Start Date */}
          <div>
            <Label className="text-sm text-white/70 mb-2 block">
              {transactionType === 'income' ? 'Next Pay Date' : 'Start Date'}
            </Label>
            <Input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="bg-white/10 border-white/20 text-white [color-scheme:dark]"
              data-testid="edit-date-input"
            />
          </div>

          {/* Category (only for bills/debt) */}
          {transactionType !== 'income' && (
            <div>
              <Label className="text-sm text-white/70 mb-2 block">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="edit-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Debt-specific fields */}
          {actualType === 'debt' && (
            <>
              <div>
                <Label className="text-sm text-white/70 mb-2 block">Total Debt Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">
                    {currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '£'}
                  </span>
                  <Input
                    type="number"
                    value={formData.totalDebt}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalDebt: e.target.value }))}
                    placeholder="Total owed"
                    className="pl-8 bg-white/10 border-white/20 text-white placeholder:text-white/30"
                    data-testid="edit-total-debt-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-white/70 mb-2 block">Interest Rate (APR %)</Label>
                <Input
                  type="number"
                  value={formData.interestRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, interestRate: e.target.value }))}
                  placeholder="e.g. 19.9"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/30"
                  data-testid="edit-interest-input"
                />
              </div>
            </>
          )}

          {/* Status indicator */}
          {formData.status === 'paused' && (
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <Pause className="w-4 h-4" />
                This entry is currently paused
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-white/10 space-y-3">
        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={loading}
          className="w-full py-4 bg-white text-slate-900 font-semibold rounded-xl hover:bg-white/90"
          data-testid="edit-save-btn"
        >
          <Save className="w-5 h-5 mr-2" />
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>

        {/* Action Row */}
        <div className="flex gap-2">
          {/* Pause/Resume Button */}
          {formData.isRecurring && (
            <Button
              variant="outline"
              onClick={handlePauseResume}
              disabled={loading}
              className={`flex-1 py-3 border-white/20 text-white hover:bg-white/10 ${
                formData.status === 'paused' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
              }`}
              data-testid="edit-pause-btn"
            >
              {formData.status === 'paused' ? (
                <>
                  <Play className="w-4 h-4 mr-2" /> Resume
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4 mr-2" /> Pause
                </>
              )}
            </Button>
          )}

          {/* Delete Button */}
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={loading}
            className="flex-1 py-3 border-red-500/30 text-red-400 hover:bg-red-500/10 bg-red-500/5"
            data-testid="edit-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Delete {formData.isRecurring ? 'Recurring Entry' : 'Entry'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {formData.isRecurring ? (
                <>
                  This will delete the entire recurring series for &quot;{formData.name}&quot;. 
                  All past and future occurrences will be removed.
                </>
              ) : (
                <>
                  Are you sure you want to delete &quot;{formData.name}&quot;? 
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side={isMobile ? "bottom" : "right"} 
        hideCloseButton={true}
        className={`p-0 border-0 bg-transparent ${isMobile ? 'h-[90vh]' : 'w-[450px] sm:max-w-[450px]'}`}
        aria-describedby={undefined}
      >
        <SheetTitle className="sr-only">Edit Transaction</SheetTitle>
        {sheetContent}
      </SheetContent>
    </Sheet>
  );
}
